// src/types/forge.tsx
export type SquadRole = 'lead' | 'market_analyst' | 'product_lead' | 'finance_lead';
export type ForgePhase = 'formation' | 'problem_selection' | 'solution_dev' | 'customer_validation' | 'graduation';
export type KPIStatus = 'not_started' | 'in_progress' | 'submitted' | 'approved' | 'rejected';
export type ProblemStatus = 'available' | 'shortlisted' | 'selected' | 'completed';
export type LOIStatus = 'draft' | 'sent' | 'signed' | 'expired';
export type MeetingType = 'discovery' | 'validation' | 'pitch' | 'follow_up';
export type ExperienceLevel = 'beginner' | 'intermediate' | 'advanced';
export type TeamSize = '2-3' | '4-5' | '6-8';

// ==============================================
// SECTOR CLASSIFICATION (Level 1 - Industry Sectors)
// ==============================================
export type IndustrySector = 
  | 'agribusiness'
  | 'manufacturing'
  | 'logistics_transport'
  | 'retail_consumer'
  | 'healthcare_pharma'
  | 'energy_utilities'
  | 'construction_real_estate'
  | 'technology_software'
  | 'financial_services'
  | 'education_training'
  | 'hospitality_tourism'
  | 'mining_extractives';

// ==============================================
// SUB-SECTOR CLASSIFICATION (Level 2 - Specific Sub-Industries)
// ==============================================
export type SubSector = 
  // Agribusiness
  | 'crop_farming'
  | 'livestock_poultry'
  | 'fisheries_aquaculture'
  | 'food_processing'
  | 'agricultural_inputs'
  | 'farm_equipment'
  // Manufacturing
  | 'fmcg_manufacturing'
  | 'automotive_assembly'
  | 'textile_apparel'
  | 'plastics_packaging'
  | 'pharmaceutical_manufacturing'
  | 'electronics_assembly'
  | 'construction_materials'
  | 'chemical_processing'
  // Logistics & Transport
  | 'freight_logistics'
  | 'cold_chain'
  | 'last_mile_delivery'
  | 'warehousing_storage'
  | 'public_transport'
  // Retail & Consumer
  | 'retail_chain'
  | 'e_commerce'
  | 'wholesale_distribution'
  | 'consumer_goods'
  // Healthcare & Pharma
  | 'hospital_clinic'
  | 'medical_devices'
  | 'pharma_distribution'
  | 'healthcare_tech'
  // Energy & Utilities
  | 'renewable_energy'
  | 'power_generation'
  | 'water_utilities'
  | 'waste_management'
  | 'energy_efficiency'
  // Technology
  | 'saas'
  | 'fintech'
  | 'agritech'
  | 'edtech'
  | 'healthtech'
  | 'cleantech'
  | 'logistics_tech'
  // Financial Services
  | 'banking'
  | 'insurance'
  | 'microfinance'
  | 'investment'
  // Education
  | 'k12_education'
  | 'vocational_training'
  | 'higher_education'
  | 'edtech_platforms';

// ==============================================
// PROBLEM CATEGORY (Level 3 - Specific Problem Types)
// ==============================================
export type ProblemCategory = 
  // Operational Efficiency
  | 'production_downtime'
  | 'quality_defects'
  | 'supply_chain_disruption'
  | 'inventory_management'
  | 'maintenance_issues'
  | 'labor_inefficiency'
  | 'capacity_utilization'
  // Cost Reduction
  | 'energy_waste'
  | 'material_waste'
  | 'labor_cost_overrun'
  | 'logistics_cost'
  | 'procurement_inefficiency'
  // Revenue Growth
  | 'customer_acquisition'
  | 'customer_retention'
  | 'pricing_strategy'
  | 'market_expansion'
  | 'product_innovation'
  // Technology & Digital
  | 'digital_transformation'
  | 'automation_opportunity'
  | 'data_analytics'
  | 'legacy_systems'
  | 'cybersecurity'
  // Sustainability & ESG
  | 'carbon_emissions'
  | 'water_conservation'
  | 'waste_reduction'
  | 'circular_economy'
  | 'sustainable_sourcing'
  // Financial & Business
  | 'working_capital'
  | 'cash_flow'
  | 'profitability'
  | 'business_model'
  | 'market_access'
  // People & Organization
  | 'talent_acquisition'
  | 'employee_retention'
  | 'skills_gap'
  | 'leadership_development'
  | 'organizational_culture'
  // Regulatory & Compliance
  | 'regulatory_compliance'
  | 'quality_certification'
  | 'safety_standards'
  | 'environmental_compliance';

// ==============================================
// TECHNICAL AREAS (Skills & Expertise)
// ==============================================
export type TechnicalArea = 
  | 'data_analytics'
  | 'software_development'
  | 'hardware_engineering'
  | 'mechanical_engineering'
  | 'electrical_engineering'
  | 'chemical_engineering'
  | 'industrial_design'
  | 'business_analytics'
  | 'financial_modeling'
  | 'market_research'
  | 'user_experience'
  | 'project_management'
  | 'supply_chain'
  | 'operations_management'
  | 'sales_marketing'
  | 'human_resources';

// ==============================================
// IMPACT AREAS (Values & Mission)
// ==============================================
export type ImpactArea = 
  | 'climate_change'
  | 'food_security'
  | 'clean_energy'
  | 'water_access'
  | 'healthcare_access'
  | 'education_access'
  | 'financial_inclusion'
  | 'gender_equality'
  | 'job_creation'
  | 'rural_development'
  | 'circular_economy'
  | 'sustainable_agriculture';

// ==============================================
// COMPANY STAGE PREFERENCES
// ==============================================
export type CompanyStage = 
  | 'early_stage_startup'
  | 'growth_stage'
  | 'established_sme'
  | 'corporate'
  | 'family_business'
  | 'non_profit'
  | 'social_enterprise';

// ==============================================
// ENGAGEMENT PREFERENCES
// ==============================================
export type EngagementPreference = 
  | 'hands_on_implementation'
  | 'strategy_consulting'
  | 'research_analysis'
  | 'product_development'
  | 'market_entry'
  | 'process_optimization'
  | 'technology_adoption'
  | 'business_model_design'
  | 'impact_measurement';

// ==============================================
// BUSINESS INTEREST (Complete Profile)
// ==============================================
export interface BusinessInterest {
  primary_sectors: IndustrySector[];
  secondary_sectors?: IndustrySector[];
  preferred_sub_sectors: SubSector[];
  problem_categories: ProblemCategory[];
  technical_areas: TechnicalArea[];
  impact_areas: ImpactArea[];
  company_stage_preferences: CompanyStage[];
  engagement_preferences: EngagementPreference[];
}

// ==============================================
// SQUAD (Core Squad Data)
// ==============================================
export interface Squad {
  id: string;
  name: string;
  squad_code: string;
  university: string;
  status: 'pending' | 'active' | 'submitted' | 'approved' | 'rejected';
  phase: ForgePhase;
  start_date: string;
  end_date: string;
  is_active: boolean;
  business_interests?: BusinessInterest;
  looking_for?: string[];
  team_size?: TeamSize;
  experience_level?: ExperienceLevel;
  created_at: string;
  updated_at: string;
}

// ==============================================
// SQUAD MEMBER
// ==============================================
export interface SquadMember {
  id: string;
  squad_id: string;
  user_id?: string;
  full_name: string;
  email: string;
  phone?: string;
  role: SquadRole;
  year_of_study?: string;
  course?: string;
  skills?: string[];
  assessment_responses?: Record<string, string>;
  assessment_completed: boolean;
  joined_at: string;
  is_lead: boolean;
  is_active: boolean;
  kpis?: {
    id: string;
    status: KPIStatus;
    value?: number;
    submitted_at?: string;
  }[];
}

// ==============================================
// KPI DEFINITION
// ==============================================
export interface KPIDefinition {
  id: string;
  role: SquadRole;
  name: string;
  description: string;
  phase: ForgePhase;
  target_value: number;
  unit: string;
}

// ==============================================
// SQUAD MEMBER KPI
// ==============================================
export interface SquadMemberKPI {
  id: string;
  squad_member_id: string;
  kpi_definition_id: string;
  target_value: number;
  actual_value?: number;
  status: KPIStatus;
  evidence_url?: string;
  submitted_at?: string;
  approved_at?: string;
  feedback?: string;
  created_at: string;
  updated_at: string;
  definition?: KPIDefinition;
}

// ==============================================
// PROBLEM (From Problem Bank)
// ==============================================
export interface Problem {
  id: string;
  title: string;
  description: string;
  company_name: string;
  company_description?: string;
  industry_sector: string;
  sub_sector?: string;
  categories?: string[];
  problem_statement: string;
  expected_outcome?: string;
  challenges: string[];
  constraints?: string[];
  resources_provided?: string[];
  contact_email: string;
  engagement_type: string;
  commitment_hours?: number;
  duration_weeks?: number;
  status: ProblemStatus;
  is_active: boolean;
  view_count: number;
  interest_count: number;
  categories?: string[];
  industry_sector?: string;
  created_at: string;
}

// ==============================================
// SQUAD PROBLEM SELECTION
// ==============================================
export interface SquadProblemSelection {
  id: string;
  squad_id: string;
  problem_id: string;
  status: ProblemStatus;
  selected_at: string;
  started_at?: string;
  completed_at?: string;
  notes?: string;
  problem?: Problem;
}

// ==============================================
// CUSTOMER MEETING
// ==============================================
export interface CustomerMeeting {
  id: string;
  squad_id: string;
  problem_id?: string;
  company_name: string;
  contact_person: string;
  contact_email: string;
  meeting_type: MeetingType;
  meeting_date: string;
  summary: string;
  key_insights: string[];
  next_steps: string[];
  created_by: string;
  created_at: string;
}

// ==============================================
// LETTER OF INTENT
// ==============================================
export interface LetterOfIntent {
  id: string;
  squad_id: string;
  company_name: string;
  company_representative: string;
  representative_title: string;
  content: string;
  status: LOIStatus;
  signed_at?: string;
  expires_at?: string;
  document_url?: string;
  created_at: string;
}

// ==============================================
// FORGE PHASE TEMPLATE
// ==============================================
export interface ForgePhaseTemplate {
  id: string;
  phase: ForgePhase;
  name: string;
  description: string;
  duration_days: number;
  required_kpis: string[];
  deliverables?: string[];
}

// ==============================================
// SQUAD PROGRESS
// ==============================================
export interface SquadProgress {
  id: string;
  squad_id: string;
  phase: ForgePhase;
  start_date: string;
  end_date?: string;
  status: 'not_started' | 'in_progress' | 'completed' | 'blocked';
  completed_at?: string;
  created_at: string;
}

// ==============================================
// SQUAD PROFILE (Complete Squad Information)
// ==============================================
export interface SquadProfile {
  id: string;
  name: string;
  squad_code: string;
  university: string;
  formation_date: string;
  team_size: TeamSize;
  team_composition: {
    roles: SquadRole[];
    disciplines: string[];
    average_experience_years: number;
  };
  business_interests: BusinessInterest;
  skills_inventory: {
    technical: TechnicalArea[];
    soft: string[];
    languages: string[];
  };
  experience_level: ExperienceLevel;
  previous_projects?: string[];
  availability: {
    hours_per_week: number;
    preferred_commitment_months: number;
    start_date?: string;
  };
  looking_for: string[];
  lead_name: string;
  lead_email: string;
  lead_phone: string;
  alternate_contact?: {
    name: string;
    email: string;
    phone: string;
  };
  culture: {
    values: string[];
    working_style: string[];
    preferred_communication: string[];
  };
  status: 'active' | 'completed' | 'inactive';
  created_at: string;
  updated_at: string;
}

// ==============================================
// PROBLEM MATCHING SCORE
// ==============================================
export interface ProblemMatchScore {
  squad_id: string;
  problem_id: string;
  scores: {
    sector_match: number;
    sub_sector_match: number;
    problem_category_match: number;
    technical_skills_match: number;
    impact_area_match: number;
    company_stage_match: number;
  };
  total_score: number;
  match_percentage: number;
  match_reasons: string[];
  suggested_roles: SquadRole[];
  recommended_engagement: EngagementPreference[];
}

// ==============================================
// PRE-DEFINED INTEREST TEMPLATES
// ==============================================
export const InterestTemplates = {
  climate_tech: {
    primary_sectors: ['energy_utilities', 'manufacturing'] as IndustrySector[],
    preferred_sub_sectors: ['renewable_energy', 'energy_efficiency', 'waste_management'] as SubSector[],
    problem_categories: ['energy_waste', 'carbon_emissions', 'sustainable_sourcing'] as ProblemCategory[],
    technical_areas: ['data_analytics', 'mechanical_engineering', 'electrical_engineering'] as TechnicalArea[],
    impact_areas: ['climate_change', 'clean_energy', 'circular_economy'] as ImpactArea[],
    company_stage_preferences: ['early_stage_startup', 'growth_stage'] as CompanyStage[],
    engagement_preferences: ['hands_on_implementation', 'technology_adoption'] as EngagementPreference[]
  },
  agritech: {
    primary_sectors: ['agribusiness'] as IndustrySector[],
    preferred_sub_sectors: ['crop_farming', 'food_processing', 'agricultural_inputs', 'agritech'] as SubSector[],
    problem_categories: ['supply_chain_disruption', 'market_access', 'sustainable_sourcing'] as ProblemCategory[],
    technical_areas: ['data_analytics', 'supply_chain', 'market_research'] as TechnicalArea[],
    impact_areas: ['food_security', 'sustainable_agriculture', 'rural_development'] as ImpactArea[],
    company_stage_preferences: ['early_stage_startup', 'established_sme'] as CompanyStage[],
    engagement_preferences: ['research_analysis', 'process_optimization'] as EngagementPreference[]
  },
  digital_transformation: {
    primary_sectors: ['technology_software', 'financial_services', 'retail_consumer'] as IndustrySector[],
    preferred_sub_sectors: ['saas', 'fintech', 'e_commerce', 'logistics_tech'] as SubSector[],
    problem_categories: ['digital_transformation', 'automation_opportunity', 'customer_acquisition'] as ProblemCategory[],
    technical_areas: ['software_development', 'data_analytics', 'user_experience'] as TechnicalArea[],
    impact_areas: ['financial_inclusion', 'job_creation'] as ImpactArea[],
    company_stage_preferences: ['growth_stage', 'corporate'] as CompanyStage[],
    engagement_preferences: ['product_development', 'technology_adoption'] as EngagementPreference[]
  },
  manufacturing_excellence: {
    primary_sectors: ['manufacturing'] as IndustrySector[],
    preferred_sub_sectors: ['fmcg_manufacturing', 'automotive_assembly', 'plastics_packaging'] as SubSector[],
    problem_categories: ['production_downtime', 'quality_defects', 'maintenance_issues', 'labor_inefficiency'] as ProblemCategory[],
    technical_areas: ['operations_management', 'mechanical_engineering', 'supply_chain'] as TechnicalArea[],
    impact_areas: ['job_creation', 'circular_economy'] as ImpactArea[],
    company_stage_preferences: ['established_sme', 'corporate'] as CompanyStage[],
    engagement_preferences: ['process_optimization', 'hands_on_implementation'] as EngagementPreference[]
  },
  healthtech: {
    primary_sectors: ['healthcare_pharma'] as IndustrySector[],
    preferred_sub_sectors: ['hospital_clinic', 'medical_devices', 'healthcare_tech'] as SubSector[],
    problem_categories: ['healthcare_access', 'regulatory_compliance', 'quality_certification'] as ProblemCategory[],
    technical_areas: ['data_analytics', 'software_development', 'business_analytics'] as TechnicalArea[],
    impact_areas: ['healthcare_access'] as ImpactArea[],
    company_stage_preferences: ['early_stage_startup', 'growth_stage'] as CompanyStage[],
    engagement_preferences: ['strategy_consulting', 'research_analysis'] as EngagementPreference[]
  }
};

// ==============================================
// HELPER FUNCTIONS
// ==============================================
export const getProblemMatchScore = (
  squadInterest: BusinessInterest,
  problemCategories: string[],
  problemIndustry: string,
  problemSubSector?: string,
  problemStage?: string
): ProblemMatchScore => {
  let sectorMatch = 0;
  let subSectorMatch = 0;
  let categoryMatch = 0;
  let technicalMatch = 0;
  let impactMatch = 0;
  let stageMatch = 0;
  const matchReasons: string[] = [];

  if (squadInterest.primary_sectors.includes(problemIndustry as IndustrySector)) {
    sectorMatch = 25;
    matchReasons.push(`Primary interest matches ${problemIndustry} sector`);
  } else if (squadInterest.secondary_sectors?.includes(problemIndustry as IndustrySector)) {
    sectorMatch = 15;
    matchReasons.push(`Secondary interest matches ${problemIndustry} sector`);
  }

  if (problemSubSector && squadInterest.preferred_sub_sectors.includes(problemSubSector as SubSector)) {
    subSectorMatch = 20;
    matchReasons.push(`Matches preferred sub-sector: ${problemSubSector}`);
  }

  const matchingCategories = problemCategories.filter(cat => 
    squadInterest.problem_categories.includes(cat as ProblemCategory)
  );
  categoryMatch = Math.min(25, Math.floor((matchingCategories.length / Math.max(1, problemCategories.length)) * 25));
  if (matchingCategories.length > 0) {
    matchReasons.push(`Matches ${matchingCategories.length} problem categories`);
  }

  const matchingSkills = squadInterest.technical_areas.filter(skill =>
    problemCategories.some(cat => cat.toLowerCase().includes(skill.toLowerCase()))
  );
  technicalMatch = Math.min(15, matchingSkills.length * 5);
  if (matchingSkills.length > 0) {
    matchReasons.push(`Skills align: ${matchingSkills.join(', ')}`);
  }

  const matchingImpacts = squadInterest.impact_areas.filter(impact =>
    problemCategories.some(cat => cat.toLowerCase().includes(impact.toLowerCase()))
  );
  impactMatch = Math.min(10, matchingImpacts.length * 3);
  if (matchingImpacts.length > 0) {
    matchReasons.push(`Impact alignment: ${matchingImpacts.join(', ')}`);
  }

  if (problemStage && squadInterest.company_stage_preferences.includes(problemStage as CompanyStage)) {
    stageMatch = 5;
    matchReasons.push(`Preferred company stage: ${problemStage}`);
  }

  const totalScore = sectorMatch + subSectorMatch + categoryMatch + technicalMatch + impactMatch + stageMatch;
  const matchPercentage = totalScore;

  return {
    squad_id: '',
    problem_id: '',
    scores: {
      sector_match: sectorMatch,
      sub_sector_match: subSectorMatch,
      problem_category_match: categoryMatch,
      technical_skills_match: technicalMatch,
      impact_area_match: impactMatch,
      company_stage_match: stageMatch
    },
    total_score: totalScore,
    match_percentage: matchPercentage,
    match_reasons: matchReasons,
    suggested_roles: [],
    recommended_engagement: squadInterest.engagement_preferences
  };
};